import { z } from 'zod';
import * as s from '#schemas/config-override/index.ts';

/** stats options shared by prometheus and graphite */
const generalStatsOptionsSchema = z.object({
  interval: s.statsIntervalSchema,
  lighthouse_metrics: s.statsLighthouseMetricsSchema,
  message_metrics: s.statsMessageMetricsSchema,
});

export const schemas = {
  preferred_ranges: z.object({ preferred_ranges: s.preferredRangesRequiredSchema }),
  'definednet.dns.resolver_addrs': z.object({
    definednet: z.object({ dns: z.object({ resolver_addrs: s.dnsResolverAddrsRequiredSchema }) }),
  }),
  'definednet.dns.match_domains': z.object({
    definednet: z.object({ dns: z.object({ match_domains: s.dnsDomainsRequiredSchema }) }),
  }),
  'definednet.dns.search_domains': z.object({
    definednet: z.object({ dns: z.object({ search_domains: s.dnsDomainsRequiredSchema }) }),
  }),
  // can't put listen.host here, because it's dynamic (requires cidr)
  'tun.disabled': z.object({ tun: z.object({ disabled: s.tunDisabledRequiredSchema }) }),
  'tun.dev': z.object({ tun: z.object({ dev: s.tunDevRequiredSchema }) }),
  'tun.mtu': z.object({ tun: z.object({ mtu: s.tunMTURequiredSchema }) }),
  'tun.tx_queue': z.object({ tun: z.object({ tx_queue: s.tunTxQueueRequiredSchema }) }),
  'tun.use_offloads': z.object({ tun: z.object({ use_offloads: s.tunUseOffloadsRequiredSchema }) }),
  'listen.batch': z.object({ listen: z.object({ batch: s.listenBatchRequiredSchema }) }),
  'listen.read_buffer': z.object({ listen: z.object({ read_buffer: s.listenReadBufferRequiredSchema }) }),
  'listen.write_buffer': z.object({ listen: z.object({ write_buffer: s.listenWriteBufferRequiredSchema }) }),
  'listen.udp_offloads': z.object({ listen: z.object({ udp_offloads: s.listenUDPOffloadsRequiredSchema }) }),
  routines: z.object({ routines: s.routinesRequiredSchema }),
  'logging.level': z.object({ logging: z.object({ level: s.loggingLevelRequiredSchema }) }),
  'logging.format': z.object({ logging: z.object({ format: s.loggingFormatRequiredSchema }) }),
  'logging.timestamp_format': z.object({
    logging: z.object({ timestamp_format: s.loggingTimestampFormatRequiredSchema }),
  }),
  'static_map.network': z.object({ static_map: z.object({ network: s.staticMapNetworkRequiredSchema }) }),
  'punchy.delay': z.object({ punchy: z.object({ delay: s.punchyDelayRequiredSchema }) }),
  'punchy.respond_delay': z.object({ punchy: z.object({ respond_delay: s.punchyRespondDelayRequiredSchema }) }),
  'lighthouse.advertise_addrs': z.object({
    lighthouse: z.object({ advertise_addrs: s.lighthouseAdvertiseAddrsRequiredSchema }),
  }),
  'lighthouse.interval': z.object({ lighthouse: z.object({ interval: s.lighthouseIntervalRequiredSchema }) }),
  prometheus: z.object({
    stats: z
      .object({
        type: z.literal('prometheus'),
        listen: s.statsListenSchema,
        path: s.statsPathSchema,
        namespace: s.statsNamespaceSchema,
        subsystem: s.statsSubsystemSchema,
      })
      .merge(generalStatsOptionsSchema),
  }),
  graphite: z.object({
    stats: z
      .object({
        type: z.literal('graphite'),
        host: s.statsHostSchema,
        prefix: s.statsPrefixSchema,
      })
      .merge(generalStatsOptionsSchema),
  }),
};

export type OverrideFormData = {
  preferred_ranges: z.input<typeof schemas.preferred_ranges>;
  'definednet.dns.resolver_addrs': z.input<(typeof schemas)['definednet.dns.resolver_addrs']>;
  'definednet.dns.match_domains': z.input<(typeof schemas)['definednet.dns.match_domains']>;
  'definednet.dns.search_domains': z.input<(typeof schemas)['definednet.dns.search_domains']>;
  'lighthouse.advertise_addrs': z.input<(typeof schemas)['lighthouse.advertise_addrs']>;
  'lighthouse.interval': z.input<(typeof schemas)['lighthouse.interval']>;
  'listen.host': { listen: { host: z.input<typeof s.requiredListenHostSchema> } };
  'tun.disabled': z.input<(typeof schemas)['tun.disabled']>;
  'tun.dev': z.input<(typeof schemas)['tun.dev']>;
  'tun.mtu': z.input<(typeof schemas)['tun.mtu']>;
  'tun.tx_queue': z.input<(typeof schemas)['tun.tx_queue']>;
  'tun.use_offloads': z.input<(typeof schemas)['tun.use_offloads']>;
  'listen.batch': z.input<(typeof schemas)['listen.batch']>;
  'listen.read_buffer': z.input<(typeof schemas)['listen.read_buffer']>;
  'listen.write_buffer': z.input<(typeof schemas)['listen.write_buffer']>;
  'listen.udp_offloads': z.input<(typeof schemas)['listen.udp_offloads']>;
  routines: z.input<(typeof schemas)['routines']>;
  'logging.level': z.input<(typeof schemas)['logging.level']>;
  'logging.format': z.input<(typeof schemas)['logging.format']>;
  'logging.timestamp_format': z.input<(typeof schemas)['logging.timestamp_format']>;
  'static_map.network': z.input<(typeof schemas)['static_map.network']>;
  'punchy.delay': z.input<(typeof schemas)['punchy.delay']>;
  'punchy.respond_delay': z.input<(typeof schemas)['punchy.respond_delay']>;
};

export type PrometheusFormData = z.input<(typeof schemas)['prometheus']>;
export type GraphiteFormData = z.input<(typeof schemas)['graphite']>;

export type OverrideFormTransformedData = {
  preferred_ranges: z.infer<typeof schemas.preferred_ranges>;
  'definednet.dns.resolver_addrs': z.infer<(typeof schemas)['definednet.dns.resolver_addrs']>;
  'definednet.dns.match_domains': z.infer<(typeof schemas)['definednet.dns.match_domains']>;
  'definednet.dns.search_domains': z.infer<(typeof schemas)['definednet.dns.search_domains']>;
  'lighthouse.advertise_addrs': z.infer<(typeof schemas)['lighthouse.advertise_addrs']>;
  'lighthouse.interval': z.infer<(typeof schemas)['lighthouse.interval']>;
  'listen.host': { listen: { host: z.infer<typeof s.listenHostSchema> } };
  'tun.disabled': z.infer<(typeof schemas)['tun.disabled']>;
  'tun.dev': z.infer<(typeof schemas)['tun.dev']>;
  'tun.mtu': z.infer<(typeof schemas)['tun.mtu']>;
  'tun.tx_queue': z.infer<(typeof schemas)['tun.tx_queue']>;
  'tun.use_offloads': z.infer<(typeof schemas)['tun.use_offloads']>;
  'listen.batch': z.infer<(typeof schemas)['listen.batch']>;
  'listen.read_buffer': z.infer<(typeof schemas)['listen.read_buffer']>;
  'listen.write_buffer': z.infer<(typeof schemas)['listen.write_buffer']>;
  'listen.udp_offloads': z.infer<(typeof schemas)['listen.udp_offloads']>;
  routines: z.infer<(typeof schemas)['routines']>;
  'logging.level': z.infer<(typeof schemas)['logging.level']>;
  'logging.format': z.infer<(typeof schemas)['logging.format']>;
  'logging.timestamp_format': z.infer<(typeof schemas)['logging.timestamp_format']>;
  'static_map.network': z.infer<(typeof schemas)['static_map.network']>;
  'punchy.delay': z.infer<(typeof schemas)['punchy.delay']>;
  'punchy.respond_delay': z.infer<(typeof schemas)['punchy.respond_delay']>;
};

// These schemas are for pre-validated config overrides (no null allowed)
export const overrideSchemas = z.discriminatedUnion('key', [
  z.object({ key: z.literal('preferred_ranges'), value: z.array(z.string()) }),
  z.object({ key: z.literal('definednet.dns.resolver_addrs'), value: z.array(z.string()) }),
  z.object({ key: z.literal('definednet.dns.match_domains'), value: z.array(z.string()) }),
  z.object({ key: z.literal('definednet.dns.search_domains'), value: z.array(z.string()) }),
  z.object({ key: z.literal('tun.disabled'), value: z.boolean() }),
  z.object({ key: z.literal('tun.dev'), value: s.tunDevRequiredSchema }),
  z.object({ key: z.literal('tun.mtu'), value: s.tunMTUStrictSchema }),
  z.object({ key: z.literal('tun.tx_queue'), value: s.tunTxQueueStrictSchema }),
  z.object({ key: z.literal('tun.use_offloads'), value: z.boolean() }),
  z.object({ key: z.literal('listen.host'), value: s.listenHostSchema }),
  z.object({ key: z.literal('listen.batch'), value: s.listenBatchStrictSchema }),
  z.object({ key: z.literal('listen.read_buffer'), value: s.listenReadBufferStrictSchema }),
  z.object({ key: z.literal('listen.write_buffer'), value: s.listenWriteBufferStrictSchema }),
  z.object({ key: z.literal('listen.udp_offloads'), value: z.boolean() }),
  z.object({ key: z.literal('routines'), value: s.routinesStrictSchema }),
  z.object({ key: z.literal('logging.level'), value: s.loggingLevelStrictSchema }),
  z.object({ key: z.literal('logging.format'), value: s.loggingFormatStrictSchema }),
  z.object({ key: z.literal('logging.timestamp_format'), value: s.loggingTimestampFormatValuesSchema }),
  z.object({ key: z.literal('lighthouse.serve_dns'), value: z.boolean() }),
  z.object({ key: z.literal('lighthouse.dns.port'), value: s.lighthouseDNSStrictPortSchema }),
  z.object({ key: z.literal('lighthouse.dns.host'), value: s.lighthouseDNSOnHostSchema }),
  z.object({ key: z.literal('lighthouse.advertise_addrs'), value: z.array(z.string()) }),
  z.object({ key: z.literal('lighthouse.interval'), value: s.lighthouseIntervalStrictSchema }),
  // TODO: these might need to be more strict, not sure yet.
  z.object({ key: z.literal('stats.host'), value: s.statsHostSchema }),
  z.object({ key: z.literal('stats.prefix'), value: s.statsPrefixSchema }),
  z.object({ key: z.literal('stats.namespace'), value: s.statsNamespaceSchema }),
  z.object({ key: z.literal('stats.subsystem'), value: s.statsSubsystemSchema }),
  z.object({ key: z.literal('stats.listen'), value: s.statsListenSchema }),
  z.object({ key: z.literal('stats.path'), value: s.statsPathSchema }),
  z.object({ key: z.literal('stats.interval'), value: s.statsIntervalStrictSchema }),
  z.object({ key: z.literal('stats.type'), value: s.statsTypeRequiredSchema }),
  z.object({ key: z.literal('stats.lighthouse_metrics'), value: z.boolean() }),
  z.object({ key: z.literal('stats.message_metrics'), value: z.boolean() }),
  // TODO: deal with allow lists
  z.object({ key: z.literal('lighthouse.remote_allow_list'), value: s.remoteAllowRuleOverrideSchema }),
  z.object({ key: z.literal('lighthouse.local_allow_list'), value: s.localAllowRuleOverrideSchema }),
  z.object({ key: z.literal('static_map.network'), value: s.staticMapNetworkStrictSchema }),
  z.object({ key: z.literal('punchy.delay'), value: s.punchyDelayStrictSchema }),
  z.object({ key: z.literal('punchy.respond_delay'), value: s.punchyRespondDelayStrictSchema }),
]);

export type TagFormOverride = z.infer<typeof overrideSchemas>;
